import { CampaignApplication } from "types/campaign/applications"
import { getErrorMessage } from "utils/helpers"
import React, { useState } from "react"
import InfluencerCardHeader from "Components/influencer/InfluencerCardHeader"
import { Button, UncontrolledPopover, PopoverBody, Spinner } from "reactstrap"
import { usePrivileges } from "hooks/useAuth"
import { PRIVILEGE } from "constants/privilege"
import { AppDispatch, RootState, store } from "store"
import { processApplication as processApplicationAPI } from "services/api/campaign/application"
import { toast } from "react-toastify"
import { useDispatch, useSelector } from "react-redux"
import { updateRemainingBudget } from "slices/campaigns/reducer"
import { toggleApplicationOffcanvas, toggleApproveApplicationCostModal } from "slices/ui/reducer"
import InfluencerStats from "Components/influencer/InfluencerStats"
import { updateApplicationStatus } from "slices/campaigns/applications/reducer"
import BookmarkIcon from "assets/icons/BookmarkIcon"
import BookmarkedIcon from "assets/icons/BookmarkedIcon"
import { bookmarkApplciation } from "slices/campaigns/applications/thunk"
import { ACCOUNT_NOT_FOUND_MSG } from "constants/global"
import Swal from "sweetalert2"
import { campaignIconSVG } from "pages/Campaigns/Campaign"
import { markFeaturesDataStale } from "slices/dashboard/reducer"

export interface ActionConfirmation {
    action: 'approve' | 'reject',
    onConfirmation: () => void
}

const ApplicationCard: React.FC<{ application: CampaignApplication, campaignId: string }> = ({ application, campaignId }) => {

    const privileges = usePrivileges();
    const dispatch: AppDispatch = useDispatch();
    const [actionConfirmation, setActionConfirmation] = useState<null | ActionConfirmation>(null);
    const [bookmarking, setBookmarking] = useState(false);
    const campaign = useSelector((state: RootState) => state.campaigns.items[campaignId]?.data?.campaign);

    // const [applicationStatus, setApplicationStatus] = useState<'pending' | 'accepted' | 'rejected' | 'processing'>(application.status);

    const applicationStatus = application.status;

    const approve = async () => {
       await processApplication(application.influencer.uuid, 'accept');
       dispatch(markFeaturesDataStale());
       dispatch(toggleApplicationOffcanvas(null))
    }

    const processApplication = async (influencerId: string, action: 'accept' | 'reject', force: boolean = false) => {
        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)) {
            toast.error("You don't have permission to process application!");
            return;
        }
        try {
            // setApplicationStatus('processing');
            dispatch(updateApplicationStatus({campaignId, applicationId: application.influencer.uuid, status: 'processing'}));
            const agencyId = store.getState().auth.selectedAgencyId;
            if (!agencyId) throw new Error("Agency not selected!");
            const result = await processApplicationAPI(agencyId, campaignId, influencerId, { action, force });
            if(result.status && result.application?.status){
                // setApplicationStatus(result.application?.status)
                dispatch(updateApplicationStatus({campaignId, applicationId: application.influencer.uuid, status: result.application.status}));
            }
            if(result.campaign?.remaining_budget){
                dispatch(updateRemainingBudget({campaignId, budget: result.campaign.remaining_budget}));
            }
        } catch (error: any) {
            if (error?.response?.data?.error_code === "existing_ongoing_collabs") {
                const r = await Swal.fire({
                    title: error?.response?.data?.message ?? "This influencer already has ongoing collaborations in other campaigns",
                    iconHtml: campaignIconSVG,
                    showCancelButton: true,
                    confirmButtonText: "Approve Anyway",
                    cancelButtonText: "Cancel",
                    reverseButtons: true
                });

                if (r.isConfirmed) {
                    try {
                        return processApplication(influencerId, action, true);
                    } catch (error: any) {
                        console.log(error);
                    }
                }
            } else {
                toast.error(getErrorMessage(error, "Failed to process application!"));
            }
            // setApplicationStatus('pending');
            dispatch(updateApplicationStatus({ campaignId, applicationId: application.influencer.uuid, status: 'pending' }));
            console.log(error);
        }
    }

    const reject = async () => {
        await processApplication(application.influencer.uuid, 'reject');
        dispatch(toggleApplicationOffcanvas(null))
    }

    const bookmark = async (value: 'y' | 'n') => {
        if (bookmarking) return;
        try {
            setBookmarking(true)
            await dispatch(bookmarkApplciation({ campaignId, influencerId: application.influencer.uuid, bookmark: value }))
        } catch (error) {
            console.log(error);
        } finally{
            setBookmarking(false)
        }
    }

    return (
        <React.Fragment>
            <div className="application-card cursor-pointer" onClick={e => {
                if(application.social_account){
                    dispatch(toggleApplicationOffcanvas({campaignId, influencerId: application.influencer?.uuid, socialAccountId: application.social_account?.uuid, status: applicationStatus, approve, reject}))
                }
            }}>
                <InfluencerCardHeader
                    name={application.influencer?.name ?? (application?.social_account?.name ?? `@${application.social_account?.handle}`)}
                    socialAccount={application.social_account}

                    language={application?.language?.name}
                    headerRightContent={
                        (
                            <div className="application-card-action d-flex flex-column gap-2">
                                <Button
                                    id={`info-popover-${application.influencer.uuid}`}
                                    type="button"
                                    onClick={e => e.stopPropagation()}
                                    className="application-action info-icon text-primary border-1 p-0"
                                >
                                    <i className="mdi mdi-information-variant" />
                                </Button>
                                <UncontrolledPopover
                                    onClick={e => e.stopPropagation()}
                                    placement="bottom"
                                    target={`info-popover-${application.influencer.uuid}`}
                                    trigger="focus"
                                    popperClassName="info-popover"
                                    transition={{ timeout: 0 }}

                                >
                                    <PopoverBody className="info-popover-body">
                                        <b>Engagement Score:</b> It is a metric that measures how often an Instagram influencer's content is interacted with, compared to how many people see it.<br /><br />
                                        {/* <b>Authenticity Score:</b> It is a measurement of how genuine and trustworthy the influencer is . It's based on the influencer's audience profile and behavior.<br /><br /> */}
                                        <b>Matching:</b> This percentage indicates how closely the influencer matches your required criteria
                                    </PopoverBody>
                                </UncontrolledPopover>
                                {
                                    application.status === 'pending' &&
                                    <span onClick={(e) => {
                                        e.stopPropagation();
                                        bookmark(application.bookmarked === 'y' ? 'n' : 'y')
                                    }}>
                                        {
                                            application.bookmarked === 'y' ?
                                            <BookmarkedIcon />
                                            : <BookmarkIcon />
                                        }
                                    </span>
                                }
                                <span className="application-action navigate-icon text-primary bg-primary-light"><i className="mdi mdi-arrow-top-right" /></span>
                            </div>
                        )
                    }
                />
                {
                    application.social_account ? (
                            <InfluencerStats 
                                average_views={application.average_views}
                                engagement_color={application.social_account?.engagement_rate_details.color}
                                engagement_score={application.social_account?.engagement_score}
                                authenticity_color={application.authenticity_score_details.color}
                                authenticity_score={application.authenticity_score}
                                requirement_match_color={application.requirement_match_details.color}
                                requirement_match={application.requirement_match}
                                followers_count={application.social_account.followers_count}
                            />
                    ) : (
                        <div className="account-not-available-card">
                            <span className="w-75">{ACCOUNT_NOT_FOUND_MSG}</span>
                        </div>
                    )
                }

                {
                    applicationStatus === 'pending' &&
                    <React.Fragment>
                        {
                            actionConfirmation ? (
                                <div className={`confirmation-bar d-flex justify-content-between align-items-center h-max-content px-3 ${actionConfirmation.action === 'reject' ? 'bg-danger' : 'bg-success'}`} onClick={e => e.stopPropagation()}>
                                    <span>Are you sure, You want to {actionConfirmation.action} ?</span>
                                    <div className="d-flex justify-content-end align-items-center gap-2">
                                        <button className="confirmation-btn" onClick={actionConfirmation.onConfirmation}>
                                            <i className='bx bx-check fs-4 text-success' />
                                        </button>
                                        <button className="confirmation-btn" onClick={e => setActionConfirmation(null)}>
                                            <i className='bx bx-x fs-4 text-danger' />
                                        </button>
                                    </div>
                                </div>
                            ) :
                                (
                                    <div className="d-flex justify-content-between align-items-center h-max-content" onClick={e => e.stopPropagation()}>
                                        <button disabled={!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)} className="btn reject application-card-action-btn btn-light-danger w-100 d-flex justify-content-center align-items-center" onClick={() => setActionConfirmation({ action: 'reject', onConfirmation: reject })}><i className='bx bx-x me-1 fs-4' ></i>Reject</button>
                                        <button disabled={!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)} className="btn approve application-card-action-btn btn-light-success w-100 d-flex justify-content-center align-items-center" onClick={() => {
                                            if(campaign?.has_transactions){
                                                dispatch(toggleApproveApplicationCostModal({application: application, isOpen: true}))
                                            }else{
                                                setActionConfirmation({ action: 'approve', onConfirmation: approve })
                                            }
                                        }}><i className='bx bx-check me-1 fs-4' ></i>Approve</button>
                                    </div>
                                )
                        }
                    </React.Fragment>
                }
                {
                    applicationStatus === 'processing' &&
                    <div className="confirmation-bar d-flex justify-content-between align-items-center h-max-content px-3" onClick={e => e.stopPropagation()}>
                        <span>Processing...</span>    
                        <Spinner size="sm" className='me-2'> Loading... </Spinner>
                    </div>
                }
                {
                    applicationStatus === 'rejected' &&
                    <div className="d-flex justify-content-center align-items-center h-max-content p-1" onClick={e => e.stopPropagation()}>
                        <button className="theme-btn rejected-btn">Rejected</button>
                    </div>
                }
                {
                    applicationStatus === 'accepted' &&
                    <div className="d-flex justify-content-center align-items-center h-max-content p-1" onClick={e => e.stopPropagation()}>
                        <button className="theme-btn approved-btn">Approved</button>
                    </div>
                }


            </div>
        </React.Fragment >

    )
}

export default ApplicationCard